import { Chip, cn } from '@sjuni/ui';
import { useQuery } from '@tanstack/react-query';
import { Check, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { apiFetch } from '@/lib/api/client';
import { useAuth } from '@/lib/auth/useAuth';

/**
 * docs/05-ADMIN-DASHBOARD.md §3's faculty tag, in its two forms.
 *
 * For a Faculty Admin it is not a picker at all: "the faculty selector is
 * locked and pre-filled", and the reason is sharper here than for staff or
 * programmes — `NewsPolicy::update()` scopes them by this exact pivot, so an
 * article that loses the tag is one its own author can no longer open. A locked
 * chip is the honest control for a field with one legal value.
 *
 * For everyone else it is a set of toggles over the four faculties, and tagging
 * is optional: university-wide news belongs to no faculty, which
 * `ScopesToFaculty` is explicit is not the same as belonging to all of them.
 *
 * **The list comes from the public endpoint.** `/admin/faculties` arrives with
 * step 6 of the build plan (task #62); until then this reads `GET /faculties`,
 * which differs in two ways worth knowing: it serves only published faculties,
 * and it resolves names to the request locale rather than sending both. Neither
 * matters for the four seeded faculties, and the Faculty Admin — the one role a
 * missing entry would lock out — never reads it.
 */
interface PublicFaculty {
  id: number;
  name: string;
  short_name: string;
}

export interface FacultyTagPickerProps {
  selected: number[];
  onChange: (next: number[]) => void;
  error?: string;
  disabled?: boolean;
}

export function FacultyTagPicker({ selected, onChange, error, disabled }: FacultyTagPickerProps) {
  const { t } = useTranslation();
  const { user, hasRole } = useAuth();

  const locked = hasRole('faculty-admin');

  const faculties = useQuery({
    queryKey: ['public', 'faculties'],
    queryFn: () => apiFetch<{ data: PublicFaculty[] }>('/faculties'),
    // Four rows that change once a decade — refetching them per editor visit
    // is a request spent on nothing.
    staleTime: 10 * 60 * 1000,
    enabled: !locked,
  });

  if (locked) {
    return (
      <div>
        <p className="text-body-sm font-semibold text-primary">{t('news.faculties.label')}</p>

        <p className="mt-2 inline-flex items-center gap-2">
          <Lock size={14} strokeWidth={1.75} aria-hidden="true" className="text-muted" />
          <Chip tone="navy">{user?.faculty?.name ?? t('news.faculties.unknown')}</Chip>
        </p>

        <p className="mt-1.5 text-caption text-muted">{t('news.faculties.lockedHint')}</p>
      </div>
    );
  }

  return (
    <fieldset>
      <legend className="text-body-sm font-semibold text-primary">
        {t('news.faculties.label')}
      </legend>
      <p className="mt-0.5 text-caption text-muted">{t('news.faculties.hint')}</p>

      <div className="mt-2 flex flex-wrap gap-2">
        {(faculties.data?.data ?? []).map((faculty) => {
          const on = selected.includes(faculty.id);

          return (
            <button
              key={faculty.id}
              type="button"
              aria-pressed={on}
              disabled={disabled}
              onClick={() =>
                onChange(
                  on ? selected.filter((id) => id !== faculty.id) : [...selected, faculty.id],
                )
              }
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-caption font-semibold transition-colors duration-fast',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600',
                'disabled:pointer-events-none disabled:opacity-40',
                on
                  ? 'border-navy-600 bg-navy-600 text-white'
                  : 'border-subtle text-secondary hover:text-primary',
              )}
            >
              {on ? <Check size={12} strokeWidth={2.5} aria-hidden="true" /> : null}
              {faculty.short_name}
            </button>
          );
        })}
      </div>

      {error ? (
        <p role="alert" className="mt-1.5 text-caption text-danger">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}
